import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'
import { parseTimeToSeconds, secondsToDisplay } from '../../utils/videoExplanation'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const AdminQuestions = () => {
  const navigate = useNavigate()
  const { testId } = useParams()
  const { user, isAdmin, adminChecked } = useAuth()
  
  const [test, setTest] = useState(null)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showImportForm, setShowImportForm] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [importing, setImporting] = useState(false)
  const [importMode, setImportMode] = useState('append') // Phase 2: 'append' | 'replace_questions' | 'validate_only'
  const [validationErrors, setValidationErrors] = useState([])
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]) // Phase 3: bulk selection
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [hasAttempts, setHasAttempts] = useState(false)
  const [attemptCount, setAttemptCount] = useState(0)
  const [showWarning, setShowWarning] = useState(false)
  const [warningAction, setWarningAction] = useState(null)
  
  // V1: Passages state
  const [activeTab, setActiveTab] = useState('questions') // 'questions' | 'passages'
  const [passages, setPassages] = useState([])
  const [showAddPassage, setShowAddPassage] = useState(false)
  const [editingPassage, setEditingPassage] = useState(null)
  const [newPassage, setNewPassage] = useState({
    title: '',
    content: '',
    order_number: 1
  })
  
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    correct_answer: '',
    points: 1,
    passage_id: '', // V1: Optional passage reference
    image_url: '', // Optional image URL
    // Per-question timestamp INTO the test-level video. Stored on backend
    // as integer seconds; kept here as the raw mm:ss / hh:mm:ss string
    // the admin typed so leading zeros and formatting are preserved
    // between blur events.
    video_explanation_time: '',
    options: [
      { key: 'A', text: '' },
      { key: 'B', text: '' },
      { key: 'C', text: '' },
      { key: 'D', text: '' }
    ]
  })

  // Inline validation errors for the video-timestamp field (add/edit).
  const [videoTimeAddError, setVideoTimeAddError] = useState('')
  const [videoTimeEditError, setVideoTimeEditError] = useState('')
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imagePreview, setImagePreview] = useState('')

  useEffect(() => {
    if (adminChecked && !isAdmin) {
      navigate('/dashboard')
    }
  }, [isAdmin, adminChecked, navigate])

  useEffect(() => {
    if (user && isAdmin && testId) {
      // Phase 2.5: clear stale local state from a previously opened test before
      // loading the new test's data so passages/questions from another test can
      // never flash in the editor during SPA navigation.
      setQuestions([])
      setPassages([])
      setTest(null)
      setSelectedQuestionIds([])
      setEditingQuestion(null)
      setEditingPassage(null)
      setNewQuestion(prev => ({ ...prev, passage_id: '' }))
      setValidationErrors([])
      setError('')
      setSuccess('')
      fetchTestAndQuestions()
    }
  }, [user, isAdmin, testId])

  const fetchTestAndQuestions = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      // Fetch test details WITH attempt count
      const testResponse = await fetch(`${BACKEND_URL}/api/admin/tests/${testId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (testResponse.ok) {
        const testData = await testResponse.json()
        setTest(testData)
        setHasAttempts(testData.has_attempts || false)
        setAttemptCount(testData.attempt_count || 0)
      }

      // Fetch questions for this test
      const questionsResponse = await fetch(`${BACKEND_URL}/api/admin/tests/${testId}/questions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (questionsResponse.ok) {
        const questionsData = await questionsResponse.json()
        setQuestions(questionsData)
      }
      
      // V1: Fetch passages for this test
      const passagesResponse = await fetch(`${BACKEND_URL}/api/admin/tests/${testId}/passages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (passagesResponse.ok) {
        const passagesData = await passagesResponse.json()
        setPassages(passagesData)
      }
    } catch (err) {
      setError('Chyba pri načítaní dát')
    } finally {
      setLoading(false)
    }
  }

  // Image upload handler
  const handleImageUpload = async (questionId, file) => {
    if (!file) return
    
    // Validate file size (2 MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Obrázok je príliš veľký. Maximálna veľkosť je 2 MB.')
      return
    }
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      setError('Nepovolený formát. Povolené sú: PNG, JPG, JPEG, SVG')
      return
    }
    
    setUploadingImage(true)
    setError('')
    
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await fetch(`${BACKEND_URL}/api/admin/questions/${questionId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })
      
      if (response.ok) {
        const data = await response.json()
        setSuccess('Obrázok bol úspešne nahraný')
        setImagePreview(data.image_url)
        
        // Update question in state
        setNewQuestion(prev => ({ ...prev, image_url: data.image_url }))
        if (editingQuestion) {
          setEditingQuestion(prev => ({ ...prev, image_url: data.image_url }))
        }
        
        // Refresh questions
        await fetchTestAndQuestions()
      } else {
        const errorData = await response.json()
        setError(errorData.detail || 'Chyba pri nahrávaní obrázka')
      }
    } catch (err) {
      setError('Chyba pri nahrávaní obrázka')
      console.error('Upload error:', err)
    } finally {
      setUploadingImage(false)
    }
  }
  
  // Image delete handler
  const handleImageDelete = async (questionId) => {
    if (!confirm('Naozaj chcete odstrániť tento obrázok?')) return
    
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      const response = await fetch(`${BACKEND_URL}/api/admin/questions/${questionId}/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        setSuccess('Obrázok bol odstránený')
        setImagePreview('')
        
        // Update question in state
        setNewQuestion(prev => ({ ...prev, image_url: '' }))
        if (editingQuestion) {
          setEditingQuestion(prev => ({ ...prev, image_url: '' }))
        }
        
        // Refresh questions
        await fetchTestAndQuestions()
      } else {
        const errorData = await response.json()
        setError(errorData.detail || 'Chyba pri odstraňovaní obrázka')
      }
    } catch (err) {
      setError('Chyba pri odstraňovaní obrázka')
      console.error('Delete error:', err)
    }
  }


  const handleAddQuestion = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      // Calculate next order_number as max(existing) + 1, not length + 1
      const nextOrderNumber = questions.length > 0 
        ? Math.max(...questions.map(q => q.order_number || 0)) + 1
        : 1

      // Prepare question data
      const questionData = {
        test_id: testId,
        question_text: newQuestion.question_text,
        question_type: newQuestion.question_type,
        correct_answer: newQuestion.correct_answer,
        order_number: nextOrderNumber,  // Use calculated value
        points: newQuestion.points,
        passage_id: newQuestion.passage_id || null, // V1: Optional passage reference
        options: newQuestion.question_type === 'multiple_choice' 
          ? newQuestion.options.filter(opt => opt.text.trim() !== '')
          : undefined
      }

      // Parse the optional video-timestamp field (mm:ss / hh:mm:ss).
      // Empty input is valid and stores null. Non-empty invalid input is
      // rejected inline; we do NOT submit the form in that case.
      const parsed = parseTimeToSeconds(newQuestion.video_explanation_time)
      if (parsed.error) {
        setVideoTimeAddError(parsed.error)
        return
      }
      setVideoTimeAddError('')
      questionData.video_explanation_start_seconds = parsed.seconds

      console.log('[AdminQuestions] 📤 Creating question:', questionData)

      const response = await fetch(`${BACKEND_URL}/api/admin/questions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(questionData)
      })

      console.log('[AdminQuestions] 📥 Response status:', response.status)

      // Read response once
      const responseText = await response.text()
      let responseData
      try {
        responseData = JSON.parse(responseText)
      } catch (e) {
        responseData = { detail: responseText }
      }

      if (response.ok) {
        console.log('[AdminQuestions] ✅ Question created:', responseData)
        setSuccess('Otázka bola pridaná')
        setShowAddForm(false)
        
        // Reset form
        setNewQuestion({
          question_text: '',
          question_type: 'multiple_choice',
          correct_answer: '',
          points: 1,
          passage_id: '', // V1: Reset passage selection
          video_explanation_time: '',
          options: [
            { key: 'A', text: '' },
            { key: 'B', text: '' },
            { key: 'C', text: '' },
            { key: 'D', text: '' }
          ]
        })
        setVideoTimeAddError('')
        
        // CRITICAL: Refresh questions list immediately
        await fetchTestAndQuestions()
      } else {
        console.error('[AdminQuestions] ❌ Error:', responseData)
        console.error('[AdminQuestions] ❌ Status:', response.status)
        
        // Show real backend error
        let errorMessage = 'Chyba pri pridávaní otázky'
        if (responseData.detail) {
          if (typeof responseData.detail === 'string') {
            if (responseData.detail.includes('duplicate key') || responseData.detail.includes('unique constraint')) {
              errorMessage = 'Otázka s týmto poradím už existuje. Skúste znova.'
            } else {
              errorMessage = responseData.detail
            }
          } else {
            errorMessage = JSON.stringify(responseData.detail)
          }
        }
        
        setError(errorMessage)
      }
    } catch (err) {
      console.error('[AdminQuestions] ❌ Exception:', err)
      setError(`Chyba pri pridávaní otázky: ${err.message}`)
    }
  }

  const updateOption = (index, text) => {
    const newOptions = [...newQuestion.options]
    newOptions[index].text = text
    setNewQuestion({ ...newQuestion, options: newOptions })
  }

  const handleDeleteQuestion = async (questionId) => {
    // Safety check for tests with attempts
    if (hasAttempts) {
      const confirmed = window.confirm(
        `⚠️ UPOZORNENIE: Tento test má ${attemptCount} pokusov používateľov!\n\n` +
        `Vymazanie otázky môže ovplyvniť konzistenciu historických výsledkov.\n\n` +
        `Naozaj chcete pokračovať?`
      )
      if (!confirmed) return
    }
    
    if (!window.confirm('Ste si istý, že chcete vymazať túto otázku?')) return

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(`${BACKEND_URL}/api/admin/questions/${questionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Immediately remove from UI (optimistic update)
        setQuestions(prev => prev.filter(q => q.id !== questionId))
        setSuccess('Otázka bola vymazaná')
        // Refresh to sync test details (total_questions count)
        await fetchTestAndQuestions()
      } else {
        setError('Chyba pri mazaní otázky')
      }
    } catch (err) {
      setError('Chyba pri mazaní otázky')
    }
  }

  const handleEditQuestion = (question) => {
    // Safety check for tests with attempts
    if (hasAttempts) {
      const confirmed = window.confirm(
        `⚠️ UPOZORNENIE: Tento test má ${attemptCount} pokusov používateľov!\n\n` +
        `Úprava otázky môže ovplyvniť konzistenciu historických výsledkov.\n\n` +
        `Naozaj chcete pokračovať?`
      )
      if (!confirmed) return
    }
    
    setEditingQuestion({
      id: question.id,
      question_text: question.question_text,
      question_type: question.question_type,
      correct_answer: question.correct_answer,
      points: question.points,
      passage_id: question.passage_id || null,
      image_url: question.image_url || '',
      // Convert stored integer seconds back into the mm:ss / hh:mm:ss
      // string the admin sees. null / undefined -> empty string.
      video_explanation_time: secondsToDisplay(question.video_explanation_start_seconds),
      options: question.options && question.options.length > 0
        ? question.options.map(opt => ({ key: opt.option_key, text: opt.option_text }))
        : [
            { key: 'A', text: '' },
            { key: 'B', text: '' },
            { key: 'C', text: '' },
            { key: 'D', text: '' }
          ]
    })
    setVideoTimeEditError('')
    setShowAddForm(false)
  }

  const handleUpdateQuestion = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const updateData = {
        question_text: editingQuestion.question_text,
        question_type: editingQuestion.question_type,
        correct_answer: editingQuestion.correct_answer,
        points: editingQuestion.points,
        passage_id: editingQuestion.passage_id || null, // V1: Allow passage assignment/removal
        options: editingQuestion.question_type === 'multiple_choice'
          ? editingQuestion.options.filter(opt => opt.text.trim() !== '')
          : undefined
      }

      // Parse and include the optional video timestamp. Empty input
      // explicitly clears the column to NULL on the backend (the payload
      // key is always sent so the update handler knows the field was
      // touched).
      const parsed = parseTimeToSeconds(editingQuestion.video_explanation_time)
      if (parsed.error) {
        setVideoTimeEditError(parsed.error)
        return
      }
      setVideoTimeEditError('')
      updateData.video_explanation_start_seconds = parsed.seconds

      const response = await fetch(`${BACKEND_URL}/api/admin/questions/${editingQuestion.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      })

      if (response.ok) {
        setSuccess('Otázka bola aktualizovaná')
        setEditingQuestion(null)
        await fetchTestAndQuestions()
      } else {
        setError('Chyba pri aktualizácii otázky')
      }
    } catch (err) {
      setError('Chyba pri aktualizácii otázky')
    }
  }

  const handleMoveQuestion = async (questionId, direction) => {
    // Safety check for tests with attempts
    if (hasAttempts) {
      const confirmed = window.confirm(
        `⚠️ UPOZORNENIE: Tento test má ${attemptCount} pokusov používateľov!\n\n` +
        `Preusporiadanie otázok môže ovplyvniť konzistenciu historických výsledkov.\n\n` +
        `Naozaj chcete pokračovať?`
      )
      if (!confirmed) return
    }
    
    const currentIndex = questions.findIndex(q => q.id === questionId)
    if (currentIndex === -1) return

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= questions.length) return

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      // Swap order numbers
      const reorderedQuestions = [...questions]
      const temp = reorderedQuestions[currentIndex]
      reorderedQuestions[currentIndex] = reorderedQuestions[newIndex]
      reorderedQuestions[newIndex] = temp

      // Update order numbers
      const updates = reorderedQuestions.map((q, idx) => ({
        id: q.id,
        order_number: idx + 1
      }))

      const response = await fetch(`${BACKEND_URL}/api/admin/questions/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ questions: updates })
      })

      if (response.ok) {
        await fetchTestAndQuestions()
      } else {
        setError('Chyba pri preusporiadaní otázok')
      }
    } catch (err) {
      setError('Chyba pri preusporiadaní otázok')
    }
  }

  const updateEditOption = (index, text) => {
    const newOptions = [...editingQuestion.options]
    newOptions[index].text = text
    setEditingQuestion({ ...editingQuestion, options: newOptions })
  }

  const handleImportJSON = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setValidationErrors([])
    
    // Phase 2: strong confirmation for replace_questions mode
    if (importMode === 'replace_questions') {
      const baseMsg =
        'Nahradiť všetky otázky?\n\n' +
        'Táto operácia VYMAŽE všetky existujúce otázky v tomto teste ' +
        'a importuje otázky z JSON súboru.\n\n' +
        'JSON sa najprv validuje; zmažeme len ak validácia prejde.'
      const attemptsWarning = hasAttempts
        ? `\n\n⚠️ UPOZORNENIE: Tento test už má ${attemptCount} pokusov používateľov! ` +
          'Nahradenie otázok môže spôsobiť, že historické výsledky budú ťažko interpretovateľné.'
        : ''
      if (!window.confirm(baseMsg + attemptsWarning + '\n\nPokračovať?')) return
    } else if (importMode === 'append' && hasAttempts) {
      // Preserve existing append-with-attempts warning
      const firstConfirm = window.confirm(
        `⚠️⚠️ KRITICKÉ UPOZORNENIE ⚠️⚠️\n\n` +
        `Tento test má ${attemptCount} pokusov používateľov!\n\n` +
        `Import otázok z JSON môže VÝRAZNE ovplyvniť historické výsledky.\n\n` +
        `Naozaj chcete pokračovať?`
      )
      if (!firstConfirm) return
      const secondConfirm = window.confirm(
        `⚠️ FINÁLNE POTVRDENIE ⚠️\n\n` +
        `Importované otázky sa pridajú k existujúcim otázkam.\n` +
        `Historické výsledky môžu stratiť konzistenciu.\n\n` +
        `Potvrďte, že chcete importovať otázky napriek riziku.`
      )
      if (!secondConfirm) return
    }
    
    setImporting(true)

    const fileInput = e.target.querySelector('input[type="file"]')
    const file = fileInput?.files[0]

    if (!file) {
      setError('Prosím vyberte JSON súbor')
      setImporting(false)
      return
    }

    try {
      const fileText = await file.text()
      let jsonData
      
      try {
        jsonData = JSON.parse(fileText)
      } catch (parseError) {
        setError('Neplatný JSON formát. Skontrolujte syntax súboru.')
        setImporting(false)
        return
      }

      // Validate structure
      if (!jsonData.questions || !Array.isArray(jsonData.questions)) {
        setError('JSON musí obsahovať pole "questions"')
        setImporting(false)
        return
      }

      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(
        `${BACKEND_URL}/api/admin/tests/${testId}/questions/import?mode=${importMode}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(jsonData)
        }
      )

      const responseText = await response.text()
      let responseData
      try {
        responseData = JSON.parse(responseText)
      } catch (e) {
        responseData = { detail: responseText }
      }

      if (response.ok) {
        if (importMode === 'validate_only') {
          if (responseData.success) {
            setSuccess(`✅ Validácia OK — ${responseData.question_count} otázok je v poriadku.`)
          } else {
            setValidationErrors(responseData.errors || [])
            setError(`Validácia našla ${responseData.errors?.length || 0} chýb (nič sa neuložilo).`)
          }
        } else {
          const extra = responseData.deleted_count
            ? ` (nahradené ${responseData.deleted_count} pôvodných)`
            : ''
          setSuccess(`✅ Importované ${responseData.imported_count} otázok${extra}`)
          setShowImportForm(false)
          if (fileInput) fileInput.value = ''
          await fetchTestAndQuestions()
        }
      } else {
        // Backend can return {errors: [...]} inside detail when validation fails for write modes
        if (responseData.detail && typeof responseData.detail === 'object' && Array.isArray(responseData.detail.errors)) {
          setValidationErrors(responseData.detail.errors)
          setError(`Import zlyhal — ${responseData.detail.errors.length} chýb v JSONe (nič sa neuložilo).`)
        } else {
          setError(`Chyba pri importe: ${responseData.detail || 'Neznáma chyba'}`)
        }
      }
    } catch (err) {
      setError(`Chyba pri importe: ${err.message}`)
    } finally {
      setImporting(false)
    }
  }

  // Phase 3: Bulk delete actions
  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    )
  }

  const toggleAllQuestions = () => {
    setSelectedQuestionIds(prev =>
      prev.length === questions.length ? [] : questions.map(q => q.id)
    )
  }

  const bulkDeleteSelected = async () => {
    if (selectedQuestionIds.length === 0) return
    const attemptsNote = hasAttempts
      ? `\n\n⚠️ Tento test má ${attemptCount} pokusov — odstránenie otázok môže ovplyvniť konzistenciu historických výsledkov.`
      : ''
    if (!window.confirm(
      `Vymazať ${selectedQuestionIds.length} vybraných otázok z tohto testu?${attemptsNote}\n\nTáto akcia je nevratná.`
    )) return

    setBulkDeleting(true)
    setError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const res = await fetch(`${BACKEND_URL}/api/admin/questions/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ question_ids: selectedQuestionIds, test_id: testId })
      })
      if (!res.ok) {
        const e = await res.json().catch(() => ({}))
        throw new Error(e.detail || 'Chyba pri hromadnom mazaní')
      }
      const data = await res.json()
      setSuccess(`✅ Vymazaných ${data.deleted_count} otázok.`)
      setSelectedQuestionIds([])
      await fetchTestAndQuestions()
    } catch (err) {
      setError(err.message || 'Chyba pri hromadnom mazaní')
    } finally {
      setBulkDeleting(false)
    }
  }

  const deleteAllQuestions = async () => {
    if (questions.length === 0) return
    const attemptsNote = hasAttempts
      ? `\n\n⚠️ Tento test má ${attemptCount} pokusov — odstránenie všetkých otázok môže ovplyvniť konzistenciu historických výsledkov.`
      : ''
    if (!window.confirm(
      `Vymazať VŠETKÝCH ${questions.length} otázok z tohto testu?${attemptsNote}\n\nTest zostane, ale bude prázdny. Táto akcia je nevratná.`
    )) return

    setBulkDeleting(true)
    setError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const res = await fetch(`${BACKEND_URL}/api/admin/tests/${testId}/questions`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        const e = await res.json().catch(() => ({}))
        throw new Error(e.detail || 'Chyba pri mazaní otázok')
      }
      const data = await res.json()
      setSuccess(`✅ Vymazaných ${data.deleted_count} otázok.`)
      setSelectedQuestionIds([])
      await fetchTestAndQuestions()
    } catch (err) {
      setError(err.message || 'Chyba pri mazaní všetkých otázok')
    } finally {
      setBulkDeleting(false)
    }
  }

  // V1: Passage CRUD functions
  const handleAddPassage = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      const nextOrderNumber = passages.length > 0
        ? Math.max(...passages.map(p => p.order_number || 0)) + 1
        : 1
      
      const passageData = {
        test_id: testId,
        title: newPassage.title,
        content: newPassage.content,
        order_number: nextOrderNumber
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/tests/${testId}/passages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(passageData)
      })
      
      if (response.ok) {
        setSuccess('Pasáž vytvorená')
        setNewPassage({ title: '', content: '', order_number: 1 })
        setShowAddPassage(false)
        fetchTestAndQuestions()
      } else {
        setError('Chyba pri vytváraní pasáže')
      }
    } catch (err) {
      setError('Chyba pri vytváraní pasáže')
    }
  }
  
  const handleEditPassage = (passage) => {
    setEditingPassage({...passage})
    setShowAddPassage(false)
  }
  
  const handleUpdatePassage = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      const response = await fetch(`${BACKEND_URL}/api/admin/passages/${editingPassage.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editingPassage.title,
          content: editingPassage.content,
          order_number: editingPassage.order_number
        })
      })
      
      if (response.ok) {
        setSuccess('Pasáž upravená')
        setEditingPassage(null)
        fetchTestAndQuestions()
      } else {
        setError('Chyba pri úprave pasáže')
      }
    } catch (err) {
      setError('Chyba pri úprave pasáže')
    }
  }
  
  const handleDeletePassage = async (passageId) => {
    if (!window.confirm('Naozaj chcete vymazať túto pasáž? Otázky s touto pasážou stratia prepojenie, ale zostanú zachované.')) {
      return
    }
    
    setError('')
    setSuccess('')
    
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      const response = await fetch(`${BACKEND_URL}/api/admin/passages/${passageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        const result = await response.json()
        const unlinkedCount = result.linked_questions_unlinked || 0
        setSuccess(`Pasáž vymazaná${unlinkedCount > 0 ? ` (${unlinkedCount} otázok odlinkovaných)` : ''}`)
        fetchTestAndQuestions()
      } else {
        setError('Chyba pri mazaní pasáže')
      }
    } catch (err) {
      setError('Chyba pri mazaní pasáže')
    }
  }

  if (!adminChecked || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isAdmin) return null

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white py-6 mb-8">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Otázky pre test</h1>
            {test && <p className="text-blue-100 mt-1">{test.title}</p>}
          </div>
          <button
            onClick={() => navigate('/admin/tests')}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 rounded-lg transition"
          >
            ← Späť na testy
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}
        
        {/* V1: Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('questions')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'questions'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                Otázky ({questions.length})
              </button>
              <button
                onClick={() => setActiveTab('passages')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                  activeTab === 'passages'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                Pasáže ({passages.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Summary - Questions Tab */}
        {activeTab === 'questions' && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold mb-2">Otázky ({questions.length})</h2>
              <p className="text-gray-600">Spravujte otázky pre tento test</p>
              {hasAttempts && (
                <div className="mt-3 flex items-center gap-2 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-2 text-sm">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-yellow-800">Tento test má {attemptCount} pokusov používateľov</p>
                    <p className="text-yellow-700">Zmeny otázok môžu ovplyvniť historické výsledky</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowImportForm(!showImportForm)
                  setShowAddForm(false)
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
              >
                {showImportForm ? 'Zrušiť' : '📥 Import JSON'}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(!showAddForm)
                  setShowImportForm(false)
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                {showAddForm ? 'Zrušiť' : '+ Pridať otázku'}
              </button>
            </div>
          </div>
        </div>
        )}
        
        {/* V1: Summary - Passages Tab */}
        {activeTab === 'passages' && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold mb-2">Pasáže ({passages.length})</h2>
              <p className="text-gray-600">Textové pasáže/čítania s porozumením pre otázky</p>
            </div>
            <button
              onClick={() => {
                setShowAddPassage(!showAddPassage)
                setEditingPassage(null)
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              {showAddPassage ? 'Zrušiť' : '➕ Pridať pasáž'}
            </button>
          </div>
        </div>
        )}

        {/* QUESTIONS TAB CONTENT */}
        {activeTab === 'questions' && (
        <>
        {/* Import JSON Form */}
        {showImportForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-bold mb-4">Import otázok z JSON</h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 mb-2"><strong>Formát JSON:</strong></p>
              <pre className="text-xs bg-blue-100 p-3 rounded overflow-x-auto">
{`{
  "questions": [
    {
      "question_text": "Text otázky",
      "question_type": "multiple_choice",
      "correct_answer": "A",
      "points": 1,
      "options": [
        {
          "option_key": "A",
          "option_text": "Možnosť A",
          "order_number": 1
        },
        {
          "option_key": "B",
          "option_text": "Možnosť B",
          "order_number": 2
        }
      ],
      "explanation": {
        "hint_text": "Nápoveda (voliteľné)",
        "explanation_text": "Vysvetlenie (voliteľné)"
      }
    }
  ]
}`}
              </pre>
            </div>

            <form onSubmit={handleImportJSON} className="space-y-4" data-testid="import-form">
              {/* Phase 2: Import mode selector */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Režim importu
                </label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="import-mode"
                      value="append"
                      checked={importMode === 'append'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="mt-1"
                      disabled={importing}
                      data-testid="mode-append"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Pridať</strong> — importované otázky sa pridajú za existujúce (pôvodné správanie).
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="import-mode"
                      value="replace_questions"
                      checked={importMode === 'replace_questions'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="mt-1"
                      disabled={importing}
                      data-testid="mode-replace"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Nahradiť otázky</strong> — vymaže všetky existujúce otázky v tomto teste a nahradí ich novými z JSON.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="import-mode"
                      value="validate_only"
                      checked={importMode === 'validate_only'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="mt-1"
                      disabled={importing}
                      data-testid="mode-validate"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Len validovať</strong> — skontroluje JSON štruktúru, nič neuloží do DB.
                    </span>
                  </label>
                </div>
                {importMode === 'replace_questions' && (
                  <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    ⚠️ Režim <strong>Nahradiť</strong> zmaže <strong>{questions.length}</strong> existujúcich otázok.
                    JSON sa najprv validuje — zmažeme len ak validácia prejde.
                  </p>
                )}
              </div>

              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="validation-errors">
                  <p className="text-sm font-semibold text-red-800 mb-2">
                    Nájdené chyby vo validácii ({validationErrors.length}):
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 list-disc pl-5 max-h-64 overflow-y-auto">
                    {validationErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vyberte JSON súbor
                </label>
                <input
                  type="file"
                  accept=".json"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  disabled={importing}
                  data-testid="import-file"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={importing}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="import-submit"
                >
                  {importing
                    ? 'Spracovávam...'
                    : importMode === 'validate_only'
                      ? 'Validovať'
                      : importMode === 'replace_questions'
                        ? 'Nahradiť otázky'
                        : 'Importovať otázky'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowImportForm(false); setValidationErrors([]) }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Zrušiť
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Add Question Form */}
        {showAddForm && (
          <form onSubmit={handleAddQuestion} className="bg-white rounded-lg shadow p-6 mb-6 space-y-6">
            <h3 className="text-lg font-bold">Nová otázka</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text otázky *
              </label>
              <textarea
                required
                value={newQuestion.question_text}
                onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Zadajte text otázky..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Typ otázky *
                </label>
                <select
                  value={newQuestion.question_type}
                  onChange={(e) => setNewQuestion({...newQuestion, question_type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="multiple_choice">Výber z možností</option>
                  <option value="short_answer">Krátka odpoveď</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={newQuestion.points}
                  onChange={(e) => setNewQuestion({...newQuestion, points: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* V1: Passage selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pasáž (voliteľné)
              </label>
              <select
                value={newQuestion.passage_id || ''}
                onChange={(e) => setNewQuestion({...newQuestion, passage_id: e.target.value || null})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Žiadna pasáž</option>
                {passages.map(passage => (
                  <option key={passage.id} value={passage.id}>
                    {passage.title}
                  </option>
                ))}
              </select>
              {passages.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">Zatiaľ neboli vytvorené žiadne pasáže</p>
              )}
            </div>
            
            {/* Image Upload Section */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Obrázok (voliteľné)
                <span className="text-xs text-gray-500 ml-2">Pre geometrické úlohy, grafy, schémy</span>
              </label>
              
              {(imagePreview || newQuestion.image_url) ? (
                <div className="space-y-3">
                  <div className="relative border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                    <img 
                      src={imagePreview || newQuestion.image_url} 
                      alt="Náhľad otázky" 
                      className="max-w-full h-auto max-h-64 mx-auto rounded"
                    />
                  </div>
                  <div className="flex gap-2">
                    <label className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-center cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={(e) => {
                          const file = e.target.files[0]
                          if (file && newQuestion.id) {
                            handleImageUpload(newQuestion.id, file)
                          } else if (file) {
                            // Preview only for new questions (upload after creation)
                            const reader = new FileReader()
                            reader.onload = (ev) => setImagePreview(ev.target.result)
                            reader.readAsDataURL(file)
                          }
                        }}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      {uploadingImage ? 'Nahrávam...' : 'Nahradiť obrázok'}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (newQuestion.id) {
                          handleImageDelete(newQuestion.id)
                        } else {
                          setImagePreview('')
                          setNewQuestion({...newQuestion, image_url: ''})
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      disabled={uploadingImage}
                    >
                      Odstrániť
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Max. 2 MB | Formáty: PNG, JPG, JPEG, SVG</p>
                </div>
              ) : (
                <div>
                  <label className="block w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition cursor-pointer bg-gray-50 hover:bg-blue-50 text-center">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          // Preview only (upload after question is created)
                          const reader = new FileReader()
                          reader.onload = (ev) => setImagePreview(ev.target.result)
                          reader.readAsDataURL(file)
                        }
                      }}
                      className="hidden"
                    />
                    <div className="space-y-2">
                      <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-600 font-medium">Kliknite pre výber obrázka</p>
                      <p className="text-xs text-gray-500">Max. 2 MB | PNG, JPG, JPEG, SVG</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {newQuestion.question_type === 'multiple_choice' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Možnosti odpovedí
                  </label>
                  <div className="space-y-3">
                    {newQuestion.options.map((option, index) => (
                      <div key={option.key} className="flex items-center gap-3">
                        <span className="font-bold text-gray-700 w-8">{option.key})</span>
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => updateOption(index, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={`Možnosť ${option.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Správna odpoveď *
                  </label>
                  <select
                    required
                    value={newQuestion.correct_answer}
                    onChange={(e) => setNewQuestion({...newQuestion, correct_answer: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Vyberte správnu odpoveď</option>
                    {newQuestion.options.filter(opt => opt.text.trim() !== '').map(option => (
                      <option key={option.key} value={option.key}>
                        {option.key}) {option.text}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {newQuestion.question_type === 'short_answer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Správna odpoveď *
                </label>
                <input
                  type="text"
                  required
                  value={newQuestion.correct_answer}
                  onChange={(e) => setNewQuestion({...newQuestion, correct_answer: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Zadajte správnu odpoveď"
                />
              </div>
            )}

            {/* Optional per-question video timestamp. Empty = no per-question link. */}
            <div className="border-t pt-4">
              <label
                htmlFor="question-add-video-time"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Čas vysvetlenia vo videu
                <span className="text-xs text-gray-500 ml-2">(voliteľné)</span>
              </label>
              <input
                id="question-add-video-time"
                type="text"
                inputMode="numeric"
                value={newQuestion.video_explanation_time}
                onChange={(e) => {
                  setNewQuestion({...newQuestion, video_explanation_time: e.target.value})
                  if (videoTimeAddError) setVideoTimeAddError('')
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${videoTimeAddError ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="napr. 04:45"
                data-testid="question-form-video-time"
              />
              <p className="text-xs text-gray-500 mt-1">
                Zadaj čas, od ktorého začína vysvetlenie tejto úlohy v spoločnom videu.
              </p>
              {videoTimeAddError && (
                <p className="text-xs text-red-600 mt-1" data-testid="question-form-video-time-error">
                  {videoTimeAddError}
                </p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                Pridať otázku
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
              >
                Zrušiť
              </button>
            </div>
          </form>
        )}

        {/* Edit Question Form */}
        {editingQuestion && (
          <form onSubmit={handleUpdateQuestion} className="bg-white rounded-lg shadow p-6 mb-6 space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold">Upraviť otázku</h3>
              <button
                type="button"
                onClick={() => setEditingQuestion(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text otázky *
              </label>
              <textarea
                required
                value={editingQuestion.question_text}
                onChange={(e) => setEditingQuestion({...editingQuestion, question_text: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Typ *</label>
                <select
                  value={editingQuestion.question_type}
                  onChange={(e) => setEditingQuestion({...editingQuestion, question_type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="multiple_choice">Výber z možností</option>
                  <option value="short_answer">Krátka odpoveď</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Body *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={editingQuestion.points}
                  onChange={(e) => setEditingQuestion({...editingQuestion, points: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            
            {/* V1: Passage selector for edit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pasáž (voliteľné)
              </label>
              <select
                value={editingQuestion.passage_id || ''}
                onChange={(e) => setEditingQuestion({...editingQuestion, passage_id: e.target.value || null})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Žiadna pasáž</option>
                {passages.map(passage => (
                  <option key={passage.id} value={passage.id}>
                    {passage.title}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Image Upload Section - Edit Mode */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Obrázok (voliteľné)
                <span className="text-xs text-gray-500 ml-2">Pre geometrické úlohy, grafy, schémy</span>
              </label>
              
              {editingQuestion.image_url ? (
                <div className="space-y-3">
                  <div className="relative border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                    <img 
                      src={editingQuestion.image_url} 
                      alt="Náhľad otázky" 
                      className="max-w-full h-auto max-h-64 mx-auto rounded"
                    />
                  </div>
                  <div className="flex gap-2">
                    <label className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-center cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                        onChange={(e) => {
                          const file = e.target.files[0]
                          if (file) {
                            handleImageUpload(editingQuestion.id, file)
                          }
                        }}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      {uploadingImage ? 'Nahrávam...' : 'Nahradiť obrázok'}
                    </label>
                    <button
                      type="button"
                      onClick={() => handleImageDelete(editingQuestion.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      disabled={uploadingImage}
                    >
                      Odstrániť
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Max. 2 MB | Formáty: PNG, JPG, JPEG, SVG</p>
                </div>
              ) : (
                <div>
                  <label className="block w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition cursor-pointer bg-gray-50 hover:bg-blue-50 text-center">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          handleImageUpload(editingQuestion.id, file)
                        }
                      }}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <div className="space-y-2">
                      <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-gray-600 font-medium">{uploadingImage ? 'Nahrávam...' : 'Kliknite pre výber obrázka'}</p>
                      <p className="text-xs text-gray-500">Max. 2 MB | PNG, JPG, JPEG, SVG</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {editingQuestion.question_type === 'multiple_choice' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Možnosti</label>
                  <div className="space-y-3">
                    {editingQuestion.options.map((option, index) => (
                      <div key={option.key} className="flex items-center gap-3">
                        <span className="font-bold text-gray-700 w-8">{option.key})</span>
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => updateEditOption(index, e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Správna odpoveď *</label>
                  <select
                    required
                    value={editingQuestion.correct_answer}
                    onChange={(e) => setEditingQuestion({...editingQuestion, correct_answer: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Vyberte</option>
                    {editingQuestion.options.filter(opt => opt.text.trim()).map(option => (
                      <option key={option.key} value={option.key}>
                        {option.key}) {option.text}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {editingQuestion.question_type === 'short_answer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Správna odpoveď *</label>
                <input
                  type="text"
                  required
                  value={editingQuestion.correct_answer}
                  onChange={(e) => setEditingQuestion({...editingQuestion, correct_answer: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            )}

            {/* Optional per-question video timestamp — edit mode. */}
            <div className="border-t pt-4">
              <label
                htmlFor="question-edit-video-time"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Čas vysvetlenia vo videu
                <span className="text-xs text-gray-500 ml-2">(voliteľné)</span>
              </label>
              <input
                id="question-edit-video-time"
                type="text"
                inputMode="numeric"
                value={editingQuestion.video_explanation_time || ''}
                onChange={(e) => {
                  setEditingQuestion({...editingQuestion, video_explanation_time: e.target.value})
                  if (videoTimeEditError) setVideoTimeEditError('')
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${videoTimeEditError ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="napr. 04:45"
                data-testid="question-edit-video-time"
              />
              <p className="text-xs text-gray-500 mt-1">
                Zadaj čas, od ktorého začína vysvetlenie tejto úlohy v spoločnom videu.
              </p>
              {videoTimeEditError && (
                <p className="text-xs text-red-600 mt-1" data-testid="question-edit-video-time-error">
                  {videoTimeEditError}
                </p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Uložiť zmeny
              </button>
              <button
                type="button"
                onClick={() => setEditingQuestion(null)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Zrušiť
              </button>
            </div>
          </form>
        )}

        {/* Questions List */}
        {questions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">Tento test zatiaľ nemá žiadne otázky</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Pridať prvú otázku
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Phase 3: Bulk actions toolbar */}
            <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-toolbar">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedQuestionIds.length > 0 && selectedQuestionIds.length === questions.length}
                  onChange={toggleAllQuestions}
                  className="h-4 w-4 text-blue-600"
                  data-testid="bulk-select-all"
                />
                <span className="text-sm text-gray-700">
                  {selectedQuestionIds.length > 0
                    ? `Vybrané: ${selectedQuestionIds.length} / ${questions.length}`
                    : `Označiť všetky (${questions.length})`}
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={bulkDeleteSelected}
                  disabled={selectedQuestionIds.length === 0 || bulkDeleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="bulk-delete-selected"
                >
                  {bulkDeleting ? '…' : `Vymazať vybrané (${selectedQuestionIds.length})`}
                </button>
                <button
                  onClick={deleteAllQuestions}
                  disabled={bulkDeleting}
                  className="px-4 py-2 text-sm border-2 border-red-600 text-red-700 rounded hover:bg-red-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="bulk-delete-all"
                >
                  Vymazať všetky otázky
                </button>
              </div>
            </div>

            {questions.map((q, index) => (
              <div key={q.id} className="bg-white rounded-lg shadow p-6" data-testid={`question-row-${q.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedQuestionIds.includes(q.id)}
                      onChange={() => toggleQuestionSelection(q.id)}
                      className="h-4 w-4 text-blue-600"
                      data-testid={`select-question-${q.id}`}
                    />
                    <span className="text-sm font-medium text-gray-500">Otázka {index + 1}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-sm font-medium text-blue-600">{q.points} {q.points === 1 ? 'bod' : 'body'}</span>
                    {/* Reorder buttons */}
                    <button
                      onClick={() => handleMoveQuestion(q.id, 'up')}
                      disabled={index === 0}
                      className="px-2 py-1 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Posunúť hore"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleMoveQuestion(q.id, 'down')}
                      disabled={index === questions.length - 1}
                      className="px-2 py-1 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Posunúť dole"
                    >
                      ▼
                    </button>
                    {/* Edit/Delete buttons */}
                    <button
                      onClick={() => handleEditQuestion(q)}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Upraviť
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(q.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Vymazať
                    </button>
                  </div>
                </div>
                <p className="text-gray-900 font-medium mb-3 whitespace-pre-wrap">{q.question_text}</p>
                {q.passage && (
                  <div className="mb-3 text-sm bg-blue-50 border border-blue-200 rounded px-3 py-2">
                    <span className="text-blue-700 font-medium">📖 Pasáž: </span>
                    <span className="text-blue-600">{q.passage.title}</span>
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="bg-gray-100 px-3 py-1 rounded">
                    {q.question_type === 'multiple_choice' ? 'Výber z možností' : 'Krátka odpoveď'}
                  </span>
                  <span className="text-green-600 font-medium">
                    Správna odpoveď: {q.correct_answer}
                  </span>
                </div>
                {q.options && q.options.length > 0 && (
                  <div className="mt-3 pl-4 space-y-1">
                    {q.options.map(opt => (
                      <div key={opt.option_key} className="text-sm text-gray-600">
                        {opt.option_key}) {opt.option_text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </>
      )}
      
      {/* PASSAGES TAB CONTENT */}
      {activeTab === 'passages' && (
      <>
        {/* Add Passage Form */}
        {showAddPassage && (
          <form onSubmit={handleAddPassage} className="bg-white rounded-lg shadow p-6 mb-6 space-y-6">
            <h3 className="text-lg font-bold">Nová pasáž</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Názov pasáže (pre admin) *
              </label>
              <input
                type="text"
                required
                value={newPassage.title}
                onChange={(e) => setNewPassage({...newPassage, title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="napr. Úryvok 1: Príroda"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text pasáže *
              </label>
              <textarea
                required
                value={newPassage.content}
                onChange={(e) => setNewPassage({...newPassage, content: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                rows="10"
                placeholder="Zadajte text pasáže..."
              />
              <p className="text-xs text-gray-500 mt-1">Tento text sa zobrazí používateľom pri otázkach</p>
            </div>
            
            <div className="flex gap-4">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Vytvoriť pasáž
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddPassage(false)
                  setNewPassage({ title: '', content: '', order_number: 1 })
                }}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Zrušiť
              </button>
            </div>
          </form>
        )}
        
        {/* Edit Passage Form */}
        {editingPassage && (
          <form onSubmit={handleUpdatePassage} className="bg-white rounded-lg shadow p-6 mb-6 space-y-6">
            <h3 className="text-lg font-bold">Upraviť pasáž</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Názov pasáže (pre admin) *
              </label>
              <input
                type="text"
                required
                value={editingPassage.title}
                onChange={(e) => setEditingPassage({...editingPassage, title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text pasáže *
              </label>
              <textarea
                required
                value={editingPassage.content}
                onChange={(e) => setEditingPassage({...editingPassage, content: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                rows="10"
              />
            </div>
            
            <div className="flex gap-4">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Uložiť zmeny
              </button>
              <button
                type="button"
                onClick={() => setEditingPassage(null)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Zrušiť
              </button>
            </div>
          </form>
        )}
        
        {/* Passages List */}
        {passages.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">Tento test zatiaľ nemá žiadne pasáže</p>
            <button
              onClick={() => setShowAddPassage(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Pridať prvú pasáž
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {passages.map((passage, index) => (
              <div key={passage.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-500">Pasáž {index + 1}</span>
                    <h4 className="text-lg font-bold text-gray-900 mt-1">{passage.title}</h4>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditPassage(passage)}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Upraviť
                    </button>
                    <button
                      onClick={() => handleDeletePassage(passage.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Vymazať
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {passage.content.length > 300
                      ? `${passage.content.substring(0, 300)}...`
                      : passage.content}
                  </p>
                  {passage.content.length > 300 && (
                    <p className="text-xs text-gray-500 mt-2">
                      {passage.content.length} znakov celkom
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </>
      )}
      </div>
    </div>
  )
}

export default AdminQuestions
